import { castArray } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { SnPasswordAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth/services/current-user.service';
import { SnFormFieldConfig, ISnHasForm } from '@lib/form';
import { t } from '@lib/translation';
import { SnUserFieldsGeneratorService } from '@lib/users';

@Component({
  selector: 'sn-change-password-dialog',
  templateUrl: './change-password.dialog.html',
  styleUrls: ['./change-password.dialog.scss'],
})
export class SnChangePasswordDialogComponent implements ISnHasForm, OnDestroy, OnInit {

  public readonly form = new UntypedFormGroup({});
  public  formFields: SnFormFieldConfig[] = [];

  public formModel: any = {};
  public isLoading: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _matDialogRef: MatDialogRef<SnChangePasswordDialogComponent>,
    private readonly _toastrService: ToastrService,
    private readonly _passwordAPI: SnPasswordAPI,
    private readonly _userFieldsGeneratorService: SnUserFieldsGeneratorService,
    private readonly _currentUser: SnCurrentUser,
  ) {
    this.isLoading = true;
    this._passwordAPI
      .getPasswordRules(this._currentUser.email)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  public ngOnInit(): void {
    this._createFormFields();
  }

  private _createFormFields(): void {
    this.formFields = [
      {
        wrappers: ['form-field-group'],
        fieldGroup: [
          this._userFieldsGeneratorService.getCurrentPasswordField({
            templateOptions: {
              required: true,
              maxLength: 64,
            },
            validation: {
              messages: {
                maxLength: t('account.validation.password.maxLength'),
              },
            },
          }),
          this._userFieldsGeneratorService.getNewPasswordField({
            templateOptions: {
              required: true,
            },
          }),
          this._userFieldsGeneratorService.getConfirmPasswordField({
            validators: {
              mismatchedPasswords: this._mismatchPasswordsHandler.bind(this),
            },
          }),
        ],
      },
    ];
  }

  get passwordControl(): AbstractControl {
    return this.form.get('password');
  }

  get newPasswordControl(): AbstractControl {
    return this.form.get('newPassword');
  }

  get confirmPasswordControl(): AbstractControl {
    return this.form.get('confirmPassword');
  }

  get rules(): string[] {
    return this._passwordAPI.rules;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    this._passwordAPI
      .setPassword(this.passwordControl.value, this.newPasswordControl.value)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => {
          this._toastrService.success(t('account.dialogs.changePassword.success'));
          this._matDialogRef.close();
        },
        error: (error) => this._handlerServerError(error),
      });
  }

  public cancel(): void {
    this._matDialogRef.close();
  }

  private _mismatchPasswordsHandler(): boolean {
    return this.newPasswordControl.value === this.confirmPasswordControl.value;
  }

  private _handlerServerError(errorResponse: any): void {
    if (errorResponse instanceof HttpErrorResponse && errorResponse.status === 400) {
      Object.keys(errorResponse.error).forEach((key) => {
        const control = this.form.get(key);

        if (control) {
          const error = castArray(errorResponse.error[key]).join(' ');
          control.setErrors({ server: error });
        }
      });
    }
  }
}
